import { makeT, translate } from '../../group_leader/helpers/i18n'
import { isPresent } from '../../group_leader/helpers/value'

// Pure logic behind the Games step, kept out of the component so it unit-tests without React.

// Ordinals are not formulaic across locales ("1st" / "1.º" / "1位"), so each one is a key.
const FALLBACK_PLACES = 10

// The preset fixes the format, so the panel shows the allowance instead (RTM-10157, RTM-10161).
const PANEL_KEYS = [ 'holes', 'handicap', 'handicap_adjustment' ]

// The presets say `tie_breaking` where the network's level is `tie_break`.
const LEVEL_BY_OPTION_KEY = { tie_breaking: 'tie_break' }

// Every option the preset pins: `competition` and `balls` make a team game, so dropping them scored it individually.
export const presetNodeSelections = (preset) => {
  const options = preset?.options || {}

  return Object.keys(options).reduce((selections, key) => {
    const id = options[key]?.id
    if (!isPresent(id)) { return selections }

    return { ...selections, [LEVEL_BY_OPTION_KEY[key] || key]: Number(id) }
  }, {})
}

const t = makeT('group_leader.event_setup.games')

// Preset pins plus the leader's changes, minus what the traversal no longer offers (a full replace server-side).
export const saveNodeSelections = (preset, selections, nodesData) => {
  const out = presetNodeSelections(preset)

  Object.entries(selections || {}).forEach(([ key, value ]) => {
    if (!isPresent(value) || !isNodeSelectionKey(key)) { return }
    const nodes = nodesData?.[key]?.nodes
    if (!Array.isArray(nodes) || nodes.some((n) => String(n.id) === String(value))) {
      out[key] = Number(value)
    }
  })

  return out
}

// A level without `label` would show its raw key; the last fallback covers paths with no window.I18n.
const levelLabel = (key, level) => {
  const humanized = key.charAt(0).toUpperCase() + key.slice(1)
  return level?.label || translate(`models.level.labels.${key}`, { defaultValue: humanized }) || humanized
}

// A spec's int_name as a heading ("best_ball" -> "Best Ball"), for a saved game no preset matches.
export const specDisplayName = (intName) => (intName || '').split('_')
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ')

export const fallbackPlaceLabels = () =>
  Array.from({ length: FALLBACK_PLACES }, (_, i) => t(`place_${i + 1}`))

// Initials for a game format's tile ("Better Ball" -> "BB"), not a person's.
export const presetInitials = (name) => {
  // Drop empty words: a leading or trailing "-" or "/" left one, rendering "UNDEFINEDB" or throwing.
  const words = (name || '').trim()
    .split(/[\s\-/]+/)
    .filter(Boolean)
  if (!words.length) { return '' }
  if (words.length === 1) { return words[0].slice(0, 2).toUpperCase() }
  return (words[0][0] + words[1][0]).toUpperCase()
}

const pillFallback = (preset) => {
  if (preset.kind === 'skins') { return t('pill_skins') }
  if (preset.kind === 'user_scored') { return t('pill_addon') }
  if (preset.group === 'team') { return t('pill_team') }
  if (preset.group === 'individual') { return t('pill_individual') }
  return t('pill_other')
}

export const pillMeta = (preset) => ({ label: preset.pill_label || pillFallback(preset) })

export const optSummary = (options) => {
  const parts = []
  if (options.holes?.name) { parts.push(options.holes.name) }
  if (options.handicap?.name) { parts.push(options.handicap.name) }
  if (options.format?.id && options.format?.name) { parts.push(options.format.name) }
  return parts.join(' · ')
}

export const extractChoices = (data, key) => {
  if (!data) { return null }
  const level = data.magic_network?.[key]
  if (!level) { return null }
  return (level.nodes || []).filter((n) => n.kind !== 'header')
}

// Valid nodes are resolved against the preset's pins; the creation context takes no preset, so it is the fallback.
const levelNodes = (validNodesData, formCtxData, key) => {
  const fromValidNodes = validNodesData?.[key]?.nodes
  if (Array.isArray(fromValidNodes)) { return fromValidNodes }
  const fromContext = formCtxData?.magic_network?.[key]?.nodes
  return Array.isArray(fromContext) ? fromContext : null
}

// A header opens a group every later node belongs to; Nassau and the hole swaps sit outside these two.
const STANDARD_HOLES_GROUPS = [ '9 Holes', '18 Holes' ]

const standardHolesNodes = (nodes) => {
  let group = null
  return nodes.filter((node) => {
    if (node.kind === 'header') {
      group = node.name
      return false
    }
    return STANDARD_HOLES_GROUPS.includes(group)
  })
}

// `holes_node_ids` pins what the graph cannot express (Nassaus, side games); each narrowing falls back, never empties.
const holesChoices = (nodes, selectable, preset, presetVal) => {
  const pinned = (preset.holes_node_ids || []).map(String)
  if (pinned.length) {
    const allowed = selectable.filter((node) => pinned.includes(String(node.id)))
    if (allowed.length) { return allowed }
    return presetVal ? [ presetVal ] : selectable
  }
  const standard = standardHolesNodes(nodes)
  return standard.length ? standard : selectable
}

// The holes the event's games agree on, so a second game matches (RTM-10072); pinned presets are not evidence.
export const eventHolesNodeId = (addedGames) => {
  const ids = (addedGames || [])
    .filter((game) => !game.preset?.holes_node_ids?.length)
    .map((game) => game.resolvedOptions?.holes?.id ?? game.preset?.options?.holes?.id)
    .filter((id) => isPresent(id))
    .map(String)
  if (!ids.length) { return null }
  return ids.every((id) => id === ids[0]) ? ids[0] : null
}

// The level names Player A..F for any format; the real count keys on scoring players (@child_hdcp_fields).
const ALLOWANCE_POSITIONS_BY_SCORERS = { 244: 2, 245: 4 }

// The widest list the monolith defines, and v3's own cap on handicap_adjustment entries.
const DEFAULT_ALLOWANCE_POSITIONS = 4

// Read from the response, never sent back: feeding it in would make the request depend on its own response.
export const resolvedScoringPlayersId = (nodesData) =>
  nodesData?.scoring_players?.nodes?.find((node) => node.selected)?.id ?? null

// Never more than the server declared or the format has; one field (or none) is the single-percentage case.
export const allowancePositionCount = (levelDef, scoringPlayersId) => {
  const declared = (levelDef?.fields || []).length
  if (declared <= 1) { return declared }
  return Math.min(declared, ALLOWANCE_POSITIONS_BY_SCORERS[Number(scoringPlayersId)] || DEFAULT_ALLOWANCE_POSITIONS)
}

// An array for flatMap: one field normally, one per player for "% of Individual Handicaps".
const allowanceField = (levelDef, scoringPlayersId = null) => {
  if (!levelDef || levelDef.visible === false) { return [] }
  const serverFields = (levelDef.fields || []).slice(0, allowancePositionCount(levelDef, scoringPlayersId))
  const single = serverFields.length <= 1
  return (single ? [ serverFields[0] ] : serverFields).map((field, idx) => ({
    key: single ? 'handicap_adjustment' : `handicap_adjustment_${idx}`,
    label: single
      ? t('fields.handicap_allowance')
      : t('fields.handicap_allowance_player', { player: String.fromCharCode(65 + idx) }),
    type: 'percent',
    defaultValue: isPresent(field?.value) ? String(field.value) : '100',
  }))
}

// Positional and fixed-length: stableford_points_hash maps 0..9 onto hole-in-one .. others.
const LOOKUP_LABEL_KEYS = [
  'hole_in_one', 'triple_eagle', 'double_eagle', 'eagle', 'birdie',
  'par', 'bogey', 'double_bogey', 'triple_bogey', 'others',
]

// The monolith's LOOKUP_V2: `lookup_fields` carries no nodes, so classic renders these from Ruby too (RTM-10530).
const LOOKUP_DEFAULTS = {
  Eagles: [ 1, 1, 1, 1, 0, 0, 0, 0, 0, 0 ],
  Birdies: [ 1, 1, 1, 1, 1, 0, 0, 0, 0, 0 ],
  Stableford: [ 6, 6, 5, 4, 3, 2, 1, 0, 0, 0 ],
  Quota: [ 6, 6, 5, 4, 3, 2, 1, 0, 0, 0 ],
}

// As specs.js.coffee:2104-2115; Deuces and Treys are absent, as the monolith hides and zeroes those rows.
export const lookupDefaultsFor = (formatName) => {
  const base = String(formatName || '').replace('Nassau-scored ', '')
  return LOOKUP_DEFAULTS[base === 'Stableford Ringerboard' ? 'Stableford' : base] || null
}

const choiceName = (choices, id) =>
  String((choices || []).find((choice) => String(choice.id) === String(id))?.name || '').trim()

// These multiply one amount by the field (holes_won.rb:63, par_relative.rb:121); elsewhere the fee is ignored.
const POT_PAYOUTS = /^(by points|by points over quota|pro-rata to skins won)$/i

export const offersPerPlayerFee = (choices, payoutTypeId) => POT_PAYOUTS.test(choiceName(choices, payoutTypeId))

export const resolvedFormatName = (nodesData) =>
  nodesData?.format?.nodes?.find((node) => node.selected)?.name ?? null

// `special_holes` is not serialized by the API at all, so it arrives undefined rather than empty.
export const normalizeLookup = (raw) => (Array.isArray(raw) ? raw.map(Number) : null)

// The scores a golfer actually meets; the rest are hidden, not dropped, as a shorter `lookup` hashes to {}.
const BASIC_LOOKUP_INDICES = [ 3, 4, 5, 6, 7 ]

// `formatName` stands in while the traversal is in flight; a preset's format is fixed anyway.
export const buildLookupFields = (nodesData, { values = null, formatName = null } = {}) => {
  const defaults = lookupDefaultsFor(resolvedFormatName(nodesData) || formatName)
  if (!defaults) { return [] }

  return defaults.map((fallback, idx) => ({
    key: `lookup_${idx}`,
    label: t(`fields.lookup.${LOOKUP_LABEL_KEYS[idx]}`),
    type: 'points',
    advanced: !BASIC_LOOKUP_INDICES.includes(idx),
    defaultValue: String(values?.[idx] ?? fallback),
  }))
}

// One pot split by points earned; the graph shows them like None, so they are matched by name.
export const isProRataPointsPayout = (choices, payoutTypeId) =>
  isPresent(payoutTypeId) && /^by Points( over .+)?$/i.test(choiceName(choices, payoutTypeId))

// Quota's own spec columns (no `quota_*` level exists): 36 standard, 39 Chicago, as the classic form.
const STARTING_QUOTA_DEFAULTS = { standard: '36', chicago: '39' }

const isQuotaFormat = (formatName) =>
  String(formatName || '').replace('Nassau-scored ', '') === 'Quota'

// Carry-forward columns stay out: a group event is a single round.
export const buildQuotaFields = (nodesData, quotaType = null, { formatName = null } = {}) => {
  if (!isQuotaFormat(resolvedFormatName(nodesData) || formatName)) { return [] }

  const type = STARTING_QUOTA_DEFAULTS[quotaType] ? quotaType : 'standard'

  return [
    {
      key: 'quota_type',
      label: t('fields.quota.type'),
      type: 'attr_select',
      // Names only: the classic labels are sentence fragments wrapped around their own input.
      choices: [
        { id: 'standard', name: t('fields.quota.standard') },
        { id: 'chicago', name: t('fields.quota.chicago') },
      ],
      defaultId: 'standard',
    },
    {
      key: 'starting_quota',
      label: t('fields.quota.starting'),
      type: 'attr_number',
      defaultValue: STARTING_QUOTA_DEFAULTS[type],
    },
  ]
}

// Positional rows and spec columns are not levels: sent as node selections they 422 the traversal.
const NON_LEVEL_KEY = /^(lookup|balls|handicap_adjustment)_\d+$/
const NON_LEVEL_KEYS = [ 'quota_type', 'starting_quota' ]

export const isNodeSelectionKey = (key) =>
  !NON_LEVEL_KEY.test(key) && !NON_LEVEL_KEYS.includes(key)

// `special_holes` has no nodes, so classic keys these rows on the `balls` selection (specs.js.coffee:2227).
export const resolvedBallsName = (nodesData) =>
  nodesData?.balls?.nodes?.find((node) => node.selected)?.name ?? null

// Every level the server resolved: the relay saves the map as sent, it does not fill levels in like v3.
export const resolvedNodeSelections = (nodesData) =>
  Object.entries(nodesData || {}).reduce((out, [ level, data ]) => {
    const selected = data?.nodes?.find((node) => node.selected)
    return selected ? { ...out, [level]: Number(selected.id) } : out
  }, {})

// By par is indexed by par (engine.rb:6517 reads `special_holes[hole_par - 1]`): slots 2..5, par 3 to 6.
const BALLS_BY_PAR = {
  first: 2, count: 4, length: 6, labelFrom: 3, headingKey: 'label_to_count_on_each_par_hole',
  labelKey: 'fields.balls.par',
}
const BALLS_BY_HOLE = {
  first: 0, count: 18, length: 18, labelFrom: 1, headingKey: 'label_to_count_on_each_hole',
}

// Classic's own heading, reused so the wizard and the spec form read the same.
const ballsHeading = (variant) =>
  translate(`assets.tournaments2.specs.${variant.headingKey}`, {
    label_mod: translate('assets.tournaments2.specs.enter_number_of_balls'),
  })

const ballsVariantFor = (ballsName) => {
  const name = String(ballsName || '')
  if (/by par|selected pars/i.test(name)) { return BALLS_BY_PAR }
  if (/by hole|selected holes/i.test(name)) { return BALLS_BY_HOLE }
  return null
}

// None for a fixed balls count (Best Ball, Scramble); an unsaved slot is 1, as a blank column means to the engine.
export const buildBallsFields = (nodesData, { values = null, ballsName = null } = {}) => {
  // The panel does not offer balls, so the preset's pin wins when the traversal names one these rows ignore.
  const variant = ballsVariantFor(resolvedBallsName(nodesData)) || ballsVariantFor(ballsName)
  if (!variant) { return [] }

  return Array.from({ length: variant.count }, (_, offset) => {
    const index = variant.first + offset
    return {
      key: `balls_${index}`,
      index,
      // Hidden behind Advanced, not dropped: `special_holes` is positional.
      advanced: true,
      label: variant.labelKey
        ? t(variant.labelKey, { n: variant.labelFrom + offset })
        : String(variant.labelFrom + offset),
      heading: ballsHeading(variant),
      type: 'balls',
      fillLength: variant.length,
      fillValue: 0,
      defaultValue: String(values?.[index] ?? 1),
    }
  })
}

// The positional rows no level maps: the scoring lookup, then quota when given, then balls.
const positionalFields = (nodesData, preset, quotaFields = []) => [
  ...buildLookupFields(nodesData, { values: preset?.lookup, formatName: preset?.options?.format?.name }),
  ...quotaFields,
  ...buildBallsFields(nodesData, { values: preset?.special_holes, ballsName: preset?.options?.balls?.name }),
]

export const buildPanelFields = (preset, formCtxData, validNodesData, eventHolesId = null) => {
  if (!preset) { return [] }
  const levelFields = PANEL_KEYS.flatMap((key) => {
    const levelDef = validNodesData?.[key] || formCtxData?.magic_network?.[key]
    if (levelDef?.kind === 'fields' || key === 'handicap_adjustment') {
      return allowanceField(levelDef, resolvedScoringPlayersId(validNodesData))
    }
    const presetVal = preset.options?.[key]
    // The preset's option shows until a payload lands, then the payload wins.
    const nodes = levelNodes(validNodesData, formCtxData, key) || []
    const selectable = nodes.filter((n) => n.kind !== 'header')
    const resolved = key === 'holes'
      ? holesChoices(nodes, selectable, preset, presetVal)
      : selectable
    const choices = resolved.length ? resolved : (presetVal ? [ presetVal ] : [])
    if (!choices.length) { return null }
    // The preset's pin, not the payload's `selected`, which these defaults produce; holes follow the event.
    const selected = choices.find((n) => n.selected)
    const eventDefault = key === 'holes' && isPresent(eventHolesId) &&
      choices.some((n) => String(n.id) === String(eventHolesId))
      ? String(eventHolesId)
      : null
    const fallbackId = isPresent(presetVal?.id) ? presetVal.id : selected?.id
    const defaultId = eventDefault || (isPresent(fallbackId) ? String(fallbackId) : null)
    return { key, label: levelLabel(key, levelDef), choices, defaultId }
  }).filter(Boolean)

  // What the preset used to pin silently (RTM-10483, RTM-10487); the simplified panel leaves out quota.
  return [ ...levelFields, ...positionalFields(validNodesData, preset) ]
}

// A custom game owns the quota columns; the Advanced panel on an existing format leaves them out, as classic does.
export const buildCustomPanelFields = (nodesData, { quotaType = null, includeQuota = true, preset = null } = {}) => {
  if (!nodesData) { return [] }
  // flatMap, or the allowance array renders as a stray empty select (RTM-10281).
  const levelFields = Object.entries(nodesData)
    .flatMap(([ key, level ]) => {
      if (key === 'handicap_adjustment' && level?.kind === 'fields') {
        return allowanceField(level, resolvedScoringPlayersId(nodesData))
      }
      if (!Array.isArray(level?.nodes) || key === 'payout') { return null }
      if (level?.visible === false && key !== 'drop_worst') { return null }
      const selectable = level.nodes.filter((n) => n.kind !== 'header')
      if (!selectable.length) { return null }
      const defaultNode = selectable.find((n) => n.selected) || selectable[0]
      // "n/a" is the graph saying this level does not apply to the chosen format.
      if (defaultNode?.name === 'n/a') { return null }
      return {
        key,
        label: levelLabel(key, level),
        // Headers ride along for the select's groups (RTM-11098); the defaults above read `selectable`.
        choices: level.nodes,
        defaultId: defaultNode ? String(defaultNode.id) : null,
      }
    })
    .filter(Boolean)

  const quotaFields = includeQuota ? buildQuotaFields(nodesData, quotaType) : []
  return [ ...levelFields, ...positionalFields(nodesData, preset, quotaFields) ]
}

const regimeLabel = (r) => {
  const map = {
    1: t('regime_group_scramble'),
    2: t('regime_alt_shot'),
    4: t('regime_individual'),
  }
  return map[r] || t('regime_other', { n: r })
}

// Mirrors the server: with no tee sheet (foursome_count 1) it strips these competitions, so a
// game needing seats cannot be saved onto a walk-on event.
const TEE_SHEET_COMPETITION = /Pair|Foursome|2 Person/i

export const competitionNeedsTeeSheet = (name) => TEE_SHEET_COMPETITION.test(name || '')

export const getRegimeConflict = (preset, existingGames) => {
  if (!isPresent(preset.regime)) { return null }
  const anchorRegimes = existingGames
    .filter((g) => g.preset.kind !== 'skins' && isPresent(g.preset.regime))
    .map((g) => g.preset.regime)
  if (!anchorRegimes.length) { return null }
  const existingRegime = Math.min(...anchorRegimes)
  if (existingRegime === preset.regime) { return null }
  return t('errors.regime_conflict', {
    existing: regimeLabel(existingRegime),
    name: preset.name,
    required: regimeLabel(preset.regime),
  })
}

// Why this preset cannot be added, or null; walk-on first, as it blocks whatever is already on the round.
export const getPresetConflict = (preset, existingGames, { isWalkon = false } = {}) => {
  if (isWalkon && competitionNeedsTeeSheet(preset?.options?.competition?.name)) {
    return t('errors.needs_tee_sheet')
  }
  return getRegimeConflict(preset, existingGames)
}

// isPresent fits node ids (0 is real), not typed numbers ('' is empty); a 0 allowance is empty, a 0 quota real.
export const isFieldFilled = (field, value) => {
  if (field.type === 'points' || field.type === 'balls') { return true }
  if (field.type === 'attr_select') { return isPresent(value) }
  if (field.type === 'attr_number') { return String(value ?? '').trim() !== '' }

  return field.type === 'percent'
    ? Number(String(value ?? '').trim()) >= 1
    : isPresent(value)
}

// The leader's choice while the level still offers it, else the default; a stale one would 422.
export const fieldValueFor = (fields, selections, key) => {
  const field = (fields || []).find((entry) => entry.key === key)
  const stored = selections?.[key]
  const stillOffered = !field?.choices ||
    field.choices.some((choice) => String(choice.id) === String(stored))

  if (isPresent(stored) && stillOffered) { return stored }

  return field?.defaultId ?? field?.defaultValue ?? null
}

// The field values both save paths send; `seed` is the preset's pinned nodes, or {} for a custom game.
export const collectFieldValues = (fields, getValue, seed = {}) => {
  const nodeSelections = { ...seed }
  const specAttrs = {}
  let handicapAdjustment
  let lookup
  let specialHoles

  fields.forEach((field) => {
    const value = getValue(field.key)
    // `lookup` is fixed-length and positional, so a blank is written as 0 rather than skipped.
    if (field.type === 'points') {
      lookup = [ ...(lookup || []), Number(String(value ?? '').trim()) || 0 ]
      return
    }
    // `special_holes` is positional but sparse, so the array is seeded to its full length.
    if (field.type === 'balls') {
      specialHoles = specialHoles || Array(field.fillLength).fill(field.fillValue)
      specialHoles[field.index] = Number(String(value ?? '').trim()) || 0
      return
    }
    // Spec columns ride under their own names, uncoerced: `quota_type` is "standard" or "chicago".
    if (field.type === 'attr_select') {
      if (isPresent(value)) { specAttrs[field.key] = String(value) }
      return
    }
    if (field.type === 'attr_number') {
      // Omitted rather than coerced: the update path preserves a column the body leaves out.
      if (isFieldFilled(field, value)) { specAttrs[field.key] = Number(value) }
      return
    }
    if (!isFieldFilled(field, value)) { return }
    // Positional, so a blank must not be skipped -- the callers refuse to save while one is blank.
    if (field.type === 'percent') {
      handicapAdjustment = [ ...(handicapAdjustment || []), Number(value) ]
      return
    }
    nodeSelections[field.key] = Number(value)
  })

  return { nodeSelections, handicapAdjustment, lookup, specialHoles, specAttrs }
}

// A summed float (20.1 + 20.2) prints its binary tail, so a fractional total gets two decimals.
export const formatMoney = (value, symbol) => `${symbol}${Number.isInteger(value) ? value : value.toFixed(2)}`

export const trimTrailingZeros = (rows) => {
  // Array.from, not map: map preserves holes, and a hole is written to the wire as null.
  const nums = Array.from(rows, (v) => parseFloat(v) || 0)
  let last = nums.length
  while (last > 0 && nums[last - 1] === 0) { last-- }
  return nums.slice(0, last)
}

// Zeros, not an empty column, or classic splits the payout type's name and reads " 2nd" as a 2 (RTM-10484).
// Untrimmed: by Nassau reads front/back/overall by position.
export const payoutRowsForSpec = (rows) => Array.from(rows, (v) => parseFloat(v) || 0)

// Purse and points for the spec body; never `payout_gross`, read only for a "Gross / Net" payout we never offer.
export const buildPayoutForSpec = ({ payoutRows, pointsRows, payoutTypeId }) => {
  const out = {}
  // Zeros only once a type is chosen (RTM-10484); with none, classic hides these fields.
  const rowsFor = isPresent(payoutTypeId) ? payoutRowsForSpec : trimTrailingZeros
  const assign = (key, rows) => {
    const values = rowsFor(rows)
    if (values.length > 0) { out[key] = values }
  }

  assign('payout', payoutRows)
  assign('points', pointsRows)
  return out
}

// Matched to its preset by scoring levels, never by name: a renamed game (RTM-10062) would lose its preset.
const IDENTITY_LEVELS = [ 'format', 'competition', 'balls' ]

// Only Net and Gross Skins tie on the three levels; holes and allowance changes are edits, not new games.
const IDENTITY_TIE_BREAK = 'handicap'

export const presetForSpec = (presets, nodeSelections) => {
  const saved = {}
  Object.entries(nodeSelections || {}).forEach(([ key, node ]) => {
    if (isPresent(node?.id)) { saved[key] = String(node.id) }
  })

  const pinsMatch = (preset, levels) => {
    const pinned = presetNodeSelections(preset)
    return levels.every((level) => isPresent(pinned[level]) && saved[level] === String(pinned[level]))
  }

  const candidates = (presets || [])
    .filter((preset) => preset.kind !== 'user_scored' && pinsMatch(preset, IDENTITY_LEVELS))
  if (candidates.length <= 1) { return candidates[0] || null }

  // Still ambiguous after the tie-break: no preset is better than the wrong one.
  return candidates.find((preset) => pinsMatch(preset, [ IDENTITY_TIE_BREAK ])) || null
}
